// Recurring checklists client (plan §4, spec F8 and §5.6). Results and actions carry the run
// version (If-Match), so a run that changed meanwhile answers 409 instead of being overwritten.
// Starting a run (or a correcting run) carries an Idempotency-Key, so a retry makes one run.
import type { Http } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  checklistDetailSchema,
  checklistPageSchema,
  checklistRunPageSchema,
  checklistRunViewSchema,
  type ChecklistDetail,
  type ChecklistPage,
  type ChecklistResult,
  type ChecklistRunStatus,
  type ChecklistRunPage,
  type ChecklistRunView,
} from './schemas/checklists.ts';

export * from './schemas/checklists.ts';

/** The API's caps on what people type (apps/api checklists/schemas.ts, LEN.site and LEN.reason). */
export const CHECKLIST_SITE_MAX = 64;
export const CHECKLIST_NOTE_MAX = 500;

export interface ChecklistPageParams {
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
}

export interface ChecklistRunListParams extends ChecklistPageParams {
  readonly status?: ChecklistRunStatus | undefined;
}

export interface StartChecklistRunInput {
  /** Calendar date of the run, "2026-11-04"; not in the future. */
  readonly runDate: string;
  /** Required when the checklist is signed by a site attester. */
  readonly site?: string;
  /** A signed run this new run corrects. */
  readonly correctsRunId?: number;
}

export interface ChecklistResultInput {
  readonly result: ChecklistResult;
  /** Required by the API for a critical fail. */
  readonly note?: string;
}

export type ChecklistRunAction = 'complete' | 'sign';

export interface ChecklistsApi {
  list(params?: ChecklistPageParams, signal?: AbortSignal): Promise<ChecklistPage>;
  get(code: string, signal?: AbortSignal): Promise<ChecklistDetail>;
  runs(
    code: string,
    params?: ChecklistRunListParams,
    signal?: AbortSignal,
  ): Promise<ChecklistRunPage>;
  startRun(
    code: string,
    input: StartChecklistRunInput,
    idempotencyKey: string,
  ): Promise<ChecklistRunView>;
  run(id: number, signal?: AbortSignal): Promise<ChecklistRunView>;
  putResult(
    id: number,
    item: number,
    version: number,
    input: ChecklistResultInput,
  ): Promise<ChecklistRunView>;
  act(id: number, action: ChecklistRunAction, version: number): Promise<ChecklistRunView>;
}

function withQuery(path: string, params: Readonly<Record<string, string | number | undefined>>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) search.set(key, String(value));
  });
  const query = search.toString();
  return query === '' ? path : `${path}?${query}`;
}

const checklistPath = (code: string): string => `/checklists/${pathSegment(code)}`;
const runPath = (id: number): string => `/checklist-runs/${pathSegment(id)}`;

// Only the fields the API knows: it refuses unknown keys, and an empty site means "no site".
function startBody(input: StartChecklistRunInput): Record<string, unknown> {
  const body: Record<string, unknown> = { runDate: input.runDate };
  if (input.site !== undefined && input.site.trim() !== '') body['site'] = input.site.trim();
  if (input.correctsRunId !== undefined) body['correctsRunId'] = input.correctsRunId;
  return body;
}

function resultRequestBody(input: ChecklistResultInput): Record<string, unknown> {
  const note = input.note?.trim() ?? '';
  return note === '' ? { result: input.result } : { result: input.result, note };
}

export function createChecklistsApi(http: Http): ChecklistsApi {
  return {
    list: (params = {}, signal) =>
      http.json(
        { method: 'GET', path: withQuery('/checklists', { ...params }), signal },
        checklistPageSchema,
      ),
    // async, so a refused code or id rejects like any other failure instead of throwing.
    get: async (code, signal) =>
      http.json({ method: 'GET', path: checklistPath(code), signal }, checklistDetailSchema),
    runs: async (code, params = {}, signal) =>
      http.json(
        { method: 'GET', path: withQuery(`${checklistPath(code)}/runs`, { ...params }), signal },
        checklistRunPageSchema,
      ),
    startRun: async (code, input, idempotencyKey) =>
      http.json(
        {
          method: 'POST',
          path: `${checklistPath(code)}/runs`,
          body: startBody(input),
          idempotencyKey,
        },
        checklistRunViewSchema,
      ),
    run: async (id, signal) =>
      http.json({ method: 'GET', path: runPath(id), signal }, checklistRunViewSchema),
    putResult: async (id, item, version, input) =>
      http.json(
        {
          method: 'PUT',
          path: `${runPath(id)}/results/${pathSegment(item)}`,
          version,
          body: resultRequestBody(input),
        },
        checklistRunViewSchema,
      ),
    act: async (id, action, version) =>
      http.json(
        {
          method: 'POST',
          path: `${runPath(id)}/actions/${pathSegment(action)}`,
          version,
          body: {},
        },
        checklistRunViewSchema,
      ),
  };
}
